import React, { useEffect, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import { matchPath } from 'react-router'
import dayjs from 'dayjs'

import RequestFormStepButtons from './stepButtons'
import RequestFormSteps from './steps'
import RequestFormStep1 from './step1'
import RequestFormStep2 from './step2'
import RequestFormStep3 from './step3'
import RequestFormStep4 from './step4'
import ValidationError from './ValidationError'

import { setFlashMessage } from '../../../actions/flashActions'

import { fetchFacility } from '../../../actions/facilitiesActions'

import { AVAILABILITY_REQUEST } from '../../../utils/constants'
import { Card } from '../../utils/Card'

const RequestFormContainer = ({ goStep }) => {
  const dispatch = useDispatch()
  const facility = useSelector(store => store.facilities.facility)
  const facilityFetched = useSelector(store => store.facilities.fetched)
  const requestFormError = useSelector(store => store.requestForm.error)
  const isAuthenticated = useSelector(store => store.session.isAuthenticated)
  const user = useSelector(store => store.user.user)
  const premium = useSelector(store => store.user.premium)
  const currentStep = useSelector(store => store.requestForm.step)
  const clone = useSelector(store => store.requestForm.clone)

  const { location } = useSelector(store => store.router)
  const availabilityRequest = useSelector(store => store.availabilityRequests.request)

  const requestForm = useFormikContext()

  const editUuid = useMemo(() => {
    const match = matchPath(location.pathname, {
      path: '/edit/:uuid',
      exact: true,
      strict: false
    })
    return match && match.params.uuid
  }, [location])

  useEffect(() => {
    editUuid && !requestForm.values.uuid && requestForm.setFieldValue('uuid', editUuid)
  }, [editUuid, requestForm])

  useEffect(() => {
    if (clone || (requestForm.values.uuid && availabilityRequest?.facilityId !== requestForm.values.facilityId)) {
      const picked = (({
        facilityId,
        dateStart,
        dateEnd,
        stayLength,
        arrivalDays,
        minLength,
        type,
        minElectric,
        water,
        sewer,
        pullthru,
        sitePremium,
        ignoreAda,
        petsAllowed,
        specificSiteIds
      }) => ({
        facilityId,
        dateStart,
        dateEnd,
        stayLength,
        arrivalDays,
        minLength,
        type,
        minElectric,
        water,
        sewer,
        pullthru,
        sitePremium,
        ignoreAda,
        petsAllowed,
        specificSiteIds
      }))(availabilityRequest)

      if (picked.dateStart) {
        picked.dateStart = dayjs(picked.dateStart).toDate()
      }
      if (picked.dateEnd) {
        picked.dateEnd = dayjs(picked.dateEnd).toDate()
      }

      requestForm.setValues(picked)
      clone && dispatch({ type: 'REQUEST_FORM_CLONE', payload: false })
    }
  }, [requestForm.values, availabilityRequest, clone])

  const facilitySlug = useMemo(() => {
    const match = matchPath(location.pathname, {
      path: '/new/:slug',
      exact: true,
      strict: false
    })
    return match && match.params.slug
  }, [location])

  const skipStep1 = useMemo(() => currentStep === 1 && (facilitySlug || editUuid), [currentStep, facilitySlug, editUuid])
  useEffect(() => skipStep1 && goStep(2), [skipStep1])

  const formNeedsFacilitySlug = useMemo(() => facilitySlug && facilitySlug !== requestForm.values.facilityId, [facilitySlug, requestForm.values.facilityId])
  useEffect(() => formNeedsFacilitySlug && requestForm.setFieldValue('facilityId', facilitySlug), [facilitySlug, requestForm, formNeedsFacilitySlug])

  const storeNeedsFacilityReset = useMemo(() => facilityFetched && requestForm.values.facilityId && facility.slug !== requestForm.values.facilityId, [facilityFetched, requestForm.values.facilityId, facility])
  useEffect(() => storeNeedsFacilityReset && dispatch({ type: 'RESET_FACILITY' }), [storeNeedsFacilityReset])

  const storeNeedsFacility = useMemo(() => !facilityFetched && requestForm.values.facilityId && facility.slug !== requestForm.values.facilityId, [facilityFetched, requestForm.values.facilityId, facility])
  useEffect(() => storeNeedsFacility && dispatch(fetchFacility(requestForm.values.facilityId, false)), [storeNeedsFacility, requestForm.values.facilityId])

  useEffect(() => {
    facility?.id &&
      !Object.keys(facility.sitesDetails.types).includes(requestForm.values.type) &&
      requestForm.setFieldValue('type', Object.keys(facility.sitesDetails.types)[0])
  }, [facility])

  if (isAuthenticated && !premium && user.active_requests_count >= 3) {
    dispatch(push('/premium'))
    dispatch(
      setFlashMessage(
        'You have reached the request limit of our free tier. Upgrade to premium for unlimited requests.',
        'error'
      )
    )
    return null
  }

  return (
    <>
      <>
        <RequestFormSteps currentStep={currentStep} goStep={goStep} />
        {currentStep === 1 && !skipStep1 && <RequestFormStep1 />}
        {currentStep === 2 && <RequestFormStep2 />}
        {currentStep === 3 && <RequestFormStep3 />}
        {currentStep === 4 && <RequestFormStep4 />}
      </>

      <ValidationError name="matchingSitesCount" />

      {requestFormError && Array.isArray(requestFormError) && (
        <Card color="red" className="mb-6">
          <div className="text-2xl font-semibold mb-3">This request cannot be created!</div>
          {requestFormError.map(error => (
            <div key={error} className="text-lg">
              {AVAILABILITY_REQUEST.errors[error] || error}
            </div>
          ))}
        </Card>
      )}

      {facilityFetched && currentStep > 1 && <RequestFormStepButtons currentStep={currentStep} />}
    </>
  )
}

export default RequestFormContainer
